import type {
	I2VAspectRatio,
	I2VModelId,
	I2VResolution,
} from "@saas/image-to-video/types";

export const TRANSMISSION_5_DANCE_PAGE_PATH =
	"/transmission-5-dance-ai-video-generator";
export const TRANSMISSION_5_DANCE_PENDING_STATE_KEY =
	"pendingTransmission5DanceState";

/** 原 reel 两人全程打码；服务端按这些 key 配原声，见 preset-audio.ts。 */
export const TRANSMISSION_5_DANCE_MOTION_PATH =
	"presets/transmission-5-dance/motion-v1.mp4";
export const TRANSMISSION_5_DANCE_MOTION_SECONDS = 11;
export const TRANSMISSION_5_DANCE_MOTION_LITE_PATH =
	"presets/transmission-5-dance/motion-lite-v1.mp4";
export const TRANSMISSION_5_DANCE_MOTION_LITE_SECONDS = 2;
export const TRANSMISSION_5_DANCE_MOTION_ASPECT_RATIO: I2VAspectRatio = "9:16";

/** 默认舞伴可以换成朋友；删除用户选的照片后恢复这张。 */
export const TRANSMISSION_5_DANCE_PARTNER_IMAGE =
	"https://assets.imagesart.ai/home/showcase/transmission-5-dance/refs/partner.jpg";

export type Transmission5DanceQualityId = "lite" | "standard" | "hd";

/** 按参考视频 + 输出计费：Lite 14、Standard 53、HD 176 积分（同 Tiny Desk）。 */
export const TRANSMISSION_5_DANCE_QUALITIES: {
	id: Transmission5DanceQualityId;
	model: I2VModelId;
	resolution: I2VResolution;
	duration: number;
}[] = [
	{ id: "lite", model: "seedance-2-mini", resolution: "480p", duration: 4 },
	{
		id: "standard",
		model: "seedance-2-mini",
		resolution: "480p",
		duration: 11,
	},
	{ id: "hd", model: "quality-2.0-ref", resolution: "720p", duration: 11 },
];

export const TRANSMISSION_5_DANCE_PRESET = {
	mode: "reference",
	defaultQualityId: "lite" as Transmission5DanceQualityId,
	aspectRatioOptions: ["9:16"] as I2VAspectRatio[],
	defaultAspectRatio: "9:16" as I2VAspectRatio,
	durationOptions: [4, 11],
	defaultDuration: 4,
	slotCount: 2,
	requiredSlots: 1,
} as const;

export const TRANSMISSION_5_DANCE_NOTE_PREFIX = "\n\nDirector's note: ";

export function buildTransmission5DancePrompt(
	templatePrompt: string,
	note: string,
): string {
	return note.trim()
		? `${templatePrompt}${TRANSMISSION_5_DANCE_NOTE_PREFIX}${note}`
		: templatePrompt;
}

/** HD（H3）：第 3 步实测一次过的写法；H3 上限 1000，留出备注空间。槽 1 → 左边舞者，槽 2 → 右边。 */
const DUO_PROMPT =
	"Character replacement in @Video 1, two dancers in a grey parking lot, faces pixelated. Replace BOTH dancers from the first frame, head to toe: @Image 1 replaces the LEFT dancer, @Image 2 replaces the RIGHT dancer. Use the exact faces, hair, skin tones, bodies and outfits from the photos; lock each identity to its side, never blend or swap them. No black T-shirt, brown trousers, black cap or striped track jacket. Each new dancer copies the original on the same side frame by frame: position, scale, footwork, arm locks, hand passes, arm swings, squats, body rolls and timing, in sync on the beat. Keep the fixed low camera, framing, overcast light, parking lot, buildings and parked cars identical. One head per person, two arms and two hands each, exactly two dancers. No blur, pixelation, text, logos or watermark.";

/** Standard（Mini）：第 3 步实测一次过的长写法（改编自竞品 Duo 模板），Mini 收 4000 字。 */
const DUO_MINI_PROMPT = `Recreate the reference video @Video 1 shot for shot: two dancers side by side in a grey parking lot on an overcast day, the same fixed low camera, framing, timing and dance moves. Both dancers' faces are pixelated in @Video 1. The ONLY change: @Image 1 replaces the dancer on the LEFT and @Image 2 replaces the dancer on the RIGHT.

Replace BOTH characters of @Video 1, head to toe: the dancer on the LEFT with the person from @Image 1, and the dancer on the RIGHT with the person from @Image 2. Use @Image 1 and @Image 2 strictly as identity and appearance references. Keep each identity consistent through the ENTIRE video. Do NOT blend their identities with the original dancers or with each other.

Each new dancer precisely follows the original dancer on the same side: exact position, scale, posture, hand gestures, body movements, choreography, trajectory and timing, frame by frame, with natural motion and accurate sync.

Preserve EVERYTHING ELSE exactly as in @Video 1: fixed camera, angle, framing, lighting, shadows, parking lot, buildings and parked cars. Do not add or remove anything. Avoid identity blending, distortion, flickering and motion artifacts.

The LEFT dancer = the person in @Image 1 in EVERY frame: exact face, skin tone, age, hair and beard as in the photo, wearing the clothes from @Image 1. NO black T-shirt and no brown trousers.

The RIGHT dancer = the person in @Image 2 in EVERY frame: exact face, skin tone, age and hair as in the photo, wearing the clothes from @Image 2. NO black cap, no dark striped track jacket, no black trousers.

Never show blur or pixelation on any face. Never drift back to the original dancers. No one from the original video stays.

The dance, on the beat as in the reference: synchronized rhythmic footwork, coordinated arm swings and relaxed body rolls, with sharp arm locks and hand passes.

One head per person, on their own shoulders. Exactly two dancers, two arms and two hands each. No text, no logos, no watermark.`;

/** Lite 只喂开头两秒，延展成四秒：同一个机位接着跳，不承诺完整编舞。 */
const DUO_LITE_PROMPT = `Character replacement in @Video 1, the opening of a two-person dance in a grey parking lot on an overcast day. Both dancers' faces are pixelated in @Video 1. Replace BOTH dancers from the first frame, head to toe: the dancer on the LEFT with the person from @Image 1, and the dancer on the RIGHT with the person from @Image 2. Use the exact faces, skin tones, hair, bodies and outfits from the photos and keep each identity locked to its side through the whole video; never blend or swap them.

The LEFT dancer wears the clothes from @Image 1: NO black T-shirt and no brown trousers. The RIGHT dancer wears the clothes from @Image 2: NO black cap, no dark striped track jacket, no black trousers.

Copy the opening moves of the original dancer on the same side, then continue the same side-by-side dance naturally for 4 seconds: synchronized rhythmic footwork, coordinated arm swings, relaxed body rolls, sharp arm locks and hand passes, on the beat. Keep the same fixed low camera, framing, overcast light, parking lot, buildings and parked cars. No new shot, no cut, no extra people.

Never show blur or pixelation on any face. One head per person, two arms and two hands each. No text, no logos, no watermark.`;

export const TRANSMISSION_5_DANCE_TEMPLATES: {
	id: "duo";
	prompt: string;
	promptByQuality: Partial<Record<Transmission5DanceQualityId, string>>;
}[] = [
	{
		id: "duo",
		prompt: DUO_PROMPT,
		promptByQuality: { lite: DUO_LITE_PROMPT, standard: DUO_MINI_PROMPT },
	},
];
